// ** React Imports
import { useEffect, useContext, useRef, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";

// ** Redux & Store & Actions
import { useSelector, useDispatch } from "react-redux";
import { getProfileAlbumGalleries } from "store/modules/user-profile/user-profile-gallery.slice";
import { getUserProfileMain } from "store/modules/user-profile/actions/get-user-profile-main.action";

// ** Third Party Components
import { useTranslation } from "react-i18next";
import { toast } from "helpers/toastWrapper";
import loadable from "@loadable/component";
import md5 from "md5";

// ** Custom Components
import Masonry from "components/layout/Masonry/Masonry";
import Paragraph from "components/typography/Paragraph";
import MasonryVideo from "components/layout/Masonry/MasonryVideo";
import MasonryImage from "components/layout/Masonry/MasonryImage";
import Block from "components/layout/Block";
import Text from "components/typography/Text";
import Loader from "components/layout/Loader";
import ScrollToTop from "components/global/ScrollToTop";
import { Icon } from "components/icons";

// ** Loadable Components
const UserGalleryViewTypeSelector = loadable(
	() => import("./UserGalleryViewTypeSelector"),
	{
		ssr: false,
	},
);

// ** Contexts
import deviceTypeContext from "contexts/deviceTypeContext";

// #####################################################

const columns = {
	phone: 3,
	tablet: 3,
	desktop: 4,
	giant: 4,
};

// #####################################################

const Album = ({ route }) => {
	const { t } = useTranslation(["userProfile"]);
	const dispatch = useDispatch();
	const user = useSelector((state) => state.userProfileMain);
	const globalUserId = useSelector((state) => state.globalUser?.id);
	const isAuth = useSelector((state) => state.globalCommon.isAuth);
	const isClient = useSelector((state) => state.globalCommon.isClient);

	const current_album = useSelector(
		(state) => state.userProfileGallery.current_album,
	);

	const location = useLocation();

	const sort = useSelector(
		(state) => state.userProfileGallery.current_album.sort,
	);

	const { albumId } = route;

	const { isPhone } = useContext(deviceTypeContext);

	const isMe = user.id === globalUserId;
	const allowPrivateMedia =
		user?.status?.friendStatus === 3 || isMe ? true : null;

	const containerRef = useRef(null);
	const triggerObserver = useRef();
	const triggerRef = useCallback(
		(node) => {
			if (current_album.loading || !("IntersectionObserver" in window))
				return;
			if (loadedMedia >= totalMedia) return null;
			if (triggerObserver.current) triggerObserver.current.disconnect();
			const marginValue =
				containerRef.current?.getBoundingClientRect().height * 0.15 ||
				600;

			triggerObserver.current = new IntersectionObserver(
				(entries) => {
					if (entries.some((entry) => entry.isIntersecting)) {
						loadMorePhotos();
					}
				},
				{ rootMargin: `0px 0px ${marginValue}px 0px` },
			);
			if (node) triggerObserver.current.observe(node);
		},
		[current_album.loading],
	);

	const albums_list = useSelector(
		(state) => state?.userProfileGallery?.albums_list,
	);
	const currentAlbum = albums_list?.data?.find((item) => item.id === albumId);

	const albumGalleryHashFromState = useSelector(
		(state) => state?.userProfileGallery?.current_album?.hash,
	);

	useEffect(() => {
		if (!allowPrivateMedia && currentAlbum?.private) {
			toast.error(t("userProfile:tooltip:privateGallery"), 1000);
			return;
		}

		const currentAlbumGalleryHash = md5(
			JSON.stringify({
				userId: `${user.id}`,
				albumId: `${albumId}`,
				sort,
			}),
		);

		if (
			!current_album.loading &&
			albumGalleryHashFromState !== currentAlbumGalleryHash
		) {
			dispatch(
				getProfileAlbumGalleries({
					albumId,
					userId: user.id,
					page: 1,
					hashMedia: null,
					hash: currentAlbumGalleryHash,
					isAuth,
					sort: "any",
					isInfinityScroll: false,
				}),
			).then((data) => {
				if (data === undefined && history?.action === "POP") {
					dispatch(
						getUserProfileMain({
							login: user.login,
							isAuth,
							throwError: "true",
						}),
					);
				}
			});
		}
	}, [albumId]);

	const loadedMedia = current_album.data.length ?? 0;

	const pagination = current_album.pagination ?? {
		totalPages: 1,
		currentPage: 1,
	};

	const videos = current_album.videos ?? 0;
	const photos = current_album.photos ?? 0;

	const totalMedia = current_album.total ?? 0;

	const hashMedia = current_album.hashMedia ?? null;

	const items = useMemo(() => {
		return current_album.data.map((media) => {
			if (media.media_type === "photo") {
				return (
					<MasonryImage
						key={media.id}
						contentId={media.id}
						contentAlbum={albumId}
						contentType={media.media_type}
						contentHeight={media.height}
						contentWidth={media.width}
						contentLikesCount={media.likes}
						imageVert={media?.media?.vert}
						locationSearch={location.search}
						userLogin={user?.login?.toLowerCase()}
						contentData={media.date_add}
						isUserGallery
						restOfPadding={35}
					/>
				);
			} else if (media.media_type === "video") {
				return (
					<MasonryVideo
						key={media.id}
						contentId={media.id}
						contentAlbum={albumId}
						contentType={media.media_type}
						contentHeight={media.height}
						contentWidth={media.width}
						contentLikesCount={media.likes}
						contentPreviewVertical={
							media?.media?.thumbs?.preview_vertical
						}
						contentSourceFull={media?.media?.video}
						contentPosterVertical={
							media?.media?.thumbs?.poster_vertical
						}
						locationSearch={location.search}
						userLogin={user?.login?.toLowerCase()}
						contentData={media.date_add}
						isUserGallery
						restOfPadding={35}
					/>
				);
			}
		});
	}, [current_album.data]);

	const loadMorePhotos = () => {
		const currentAlbumGalleryHash = md5(
			JSON.stringify({
				userId: `${user.id}`,
				albumId: `${albumId}`,
				sort,
			}),
		);

		dispatch(
			getProfileAlbumGalleries({
				albumId,
				userId: user.id,
				page: pagination.currentPage + 1,
				hashMedia,
				isAuth,
				sort,
				isInfinityScroll: true,
				hash: currentAlbumGalleryHash,
			}),
		);
	};

	const albumName = useMemo(() => {
		if (current_album.loading || !current_album.loaded) {
			return "";
		}

		const icon =
			current_album.isPrivate &&
			(user.status.friendStatus === 3 || isMe) ? (
				<Icon name="unlock" verticalAlign="text-top" />
			) : (
				current_album.isPrivate && <Icon name="lock" />
			);

		if (current_album?.name) {
			if (current_album.isPrivate) {
				return (
					<>
						{icon} {current_album.name}
					</>
				);
			}
			return current_album.name;
		}

		return current_album.isPrivate ? (
			<>
				{icon} {t("userpanel:media:album:mainPrivate")}
			</>
		) : (
			t("userpanel:media:album:main")
		);
	}, [
		current_album?.name,
		current_album?.isPrivate,
		current_album.loaded,
		current_album.loading,
	]);

	const renderNavigation = () => {
		return (
			<Block flex justifyBetween alignCenter mb={3} withColumnGap>
				<Block full style={{ minWidth: 0 }}>
					<Text
						key={albumName}
						bold
						textNowrap
						style={{
							textOverflow: "ellipsis",
							display: "block",
							overflow: "hidden",
							maxWidth: isPhone ? null : "400px",
						}}
					>
						{t("common:album")}: {albumName}
					</Text>
				</Block>

				{isClient && isAuth && (
					<UserGalleryViewTypeSelector
						albumId={albumId}
						videos={videos}
						photos={photos}
					/>
				)}
			</Block>
		);
	};

	// #####################################################

	if (!allowPrivateMedia && currentAlbum?.private) {
		return (
			<Block mx={isPhone && 4}>
				<Block>
					<Paragraph size="1rem" mb={0}>
						{t("userProfile:tooltip:privateGallery")}
					</Paragraph>
				</Block>
			</Block>
		);
	}

	if (loadedMedia === 0 && totalMedia === 0 && !current_album.loading) {
		return (
			<Block mx={isPhone && 4}>
				{renderNavigation()}
				<Block>
					<Paragraph size="1rem" mb={0}>
						{sort === "videos"
							? t("userProfile:noVideos")
							: t("userProfile:noPhotos")}
					</Paragraph>
				</Block>
			</Block>
		);
	}

	return (
		<>
			<ScrollToTop />

			<Block mx={isPhone && 4}>{renderNavigation()}</Block>

			{current_album.loading && !loadedMedia > 0 && (
				<Block flex justifyCenter mt={2} mb={2}>
					<Loader />
				</Block>
			)}

			{current_album.data.length > 0 ? (
				<Masonry columns={columns}>{items}</Masonry>
			) : null}

			{current_album.loading && loadedMedia < totalMedia && (
				<Block flex justifyCenter mt={4} mb={1}>
					<Loader />
				</Block>
			)}
			{!current_album.loading &&
				loadedMedia < totalMedia &&
				current_album.data &&
				!(
					current_album.pagination?.currentPage > 0 &&
					current_album.data?.length === 0
				) && (
					<div
						ref={triggerRef}
						style={{
							height: "1px",
							pointerEvents: "none",
						}}
					/>
				)}
		</>
	);
};

// #####################################################

export default Album;

// #####################################################
